<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import dealStatusAPI from 'dashboard/api/pipeline/dealStatus';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();
const { showAlert } = useAlert();
const store = useStore();
const submitting = ref(false);
const selectedReason = ref('');
const commonReasons = ref([]);

// Fase 4.5 — visual hint quando o pipeline desse card exige motivo.
// O bloqueio efetivo já vem do backend (422 + msg) E do frontend
// (botão `disabled` quando !selectedReason). Asterisco é só comunicação.
const reasonRequired = computed(() => {
  const pipelineId = props.item?.pipeline_id;
  if (!pipelineId) return false;
  const pipeline = store.getters['pipelines/getPipelines']?.find?.(
    p => p.id === pipelineId
  );
  return pipeline?.settings?.require_loss_reason === true;
});

async function loadCommonReasons() {
  try {
    const response = await dealStatusAPI.getCommonReasons();
    commonReasons.value = response.data.reasons;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[MarkAsLostModal] loadCommonReasons failed:', error);
    showAlert(t('PIPELINE.DEAL_STATUS.ERROR.LOAD_REASONS_FAILED'), 'error');
  }
}

// immediate: true is required — the parent mounts this modal with `v-if` while
// `show` is already true (it sets `dealStatusCard` and `showLostModal` in the
// same tick). A non-immediate watcher never fires on that first mount, so the
// reason dropdown stayed empty. immediate:true evaluates `show` at mount too.
watch(
  () => props.show,
  val => {
    if (val) {
      selectedReason.value = '';
      loadCommonReasons();
    }
  },
  { immediate: true }
);

async function handleConfirm() {
  if (submitting.value || !selectedReason.value) return;

  submitting.value = true;
  try {
    // Forward the authoritative deal-status payload on `success` so the parent
    // patches the open card AND the board store synchronously (no stale window).
    const { data } = await dealStatusAPI.markAsLost(
      props.item.id,
      selectedReason.value
    );

    showAlert(t('PIPELINE.DEAL_STATUS.SUCCESS.MARKED_LOST'));
    emit('success', data);
    emit('close');
  } catch (error) {
    showAlert(t('PIPELINE.DEAL_STATUS.ERROR.MARK_FAILED'), 'error');
  } finally {
    submitting.value = false;
  }
}

function handleCancel() {
  emit('close');
}
</script>

<template>
  <!-- Teleport + z-[9999]: render above the generic Modal wrapper (z-[9990]) that
       hosts PipelineCardDetails in the agenda / PipelineTab. Previously this modal
       rendered nested at z-50 and stayed behind the card-detail overlay, so the
       lost popup was invisible and the deal never moved to the lost stage. -->
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 dark:bg-black/70"
      @click.self="handleCancel"
    >
      <div class="w-full max-w-md rounded-2xl bg-n-solid-2 p-6 shadow-lg">
        <!-- Header -->
        <div class="mb-4 flex items-center justify-between">
          <h3 class="text-heading-2 text-n-slate-12">
            {{ $t('PIPELINE.DEAL_STATUS.LOST_MODAL.TITLE') }}
          </h3>
          <button
            :aria-label="$t('PIPELINE.DEAL_STATUS.LOST_MODAL.CLOSE')"
            class="rounded-lg p-1 text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
            @click="handleCancel"
          >
            <fluent-icon icon="dismiss" size="16" />
          </button>
        </div>

        <!-- Message -->
        <p class="mb-4 text-body-main text-n-slate-11">
          {{ $t('PIPELINE.DEAL_STATUS.LOST_MODAL.MESSAGE') }}
        </p>

        <!-- Item Info -->
        <div class="mb-4 rounded-lg bg-n-ruby-3 border border-n-ruby-9 p-4">
          <div class="text-label font-medium text-n-ruby-11">
            {{ item.item_details?.title || item.title || 'Untitled' }}
          </div>
          <div
            v-if="item.item_details?.value"
            class="mt-1 text-label-small text-n-ruby-12"
          >
            {{
              $t('PIPELINE.DEAL_STATUS.LOST_MODAL.VALUE_DISPLAY', {
                value: new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  minimumFractionDigits: 2,
                })
                  .format(Number(item.item_details.value) || 0)
                  .replace('R$', '')
                  .trim(),
              })
            }}
          </div>
        </div>

        <!-- Reason Selector -->
        <div class="mb-6">
          <label
            for="mark-lost-reason-select"
            class="mb-2 block text-label font-medium text-n-slate-12"
          >
            {{ $t('PIPELINE.DEAL_STATUS.LOST_MODAL.REASON_LABEL') }}
            <span
              v-if="reasonRequired"
              aria-hidden="true"
              class="text-n-ruby-11"
              :title="
                $t('PIPELINE.DEAL_STATUS.LOST_MODAL.REASON_REQUIRED_HINT')
              "
            >
              {{ $t('PIPELINE.DEAL_STATUS.LOST_MODAL.REQUIRED_MARKER') }}
            </span>
          </label>
          <div class="relative">
            <select
              id="mark-lost-reason-select"
              v-model="selectedReason"
              :aria-required="reasonRequired ? 'true' : undefined"
              class="w-full appearance-none bg-none rounded-lg border-0 bg-n-surface-1 py-2 pl-3 pr-10 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 transition-all duration-200 outline-n-weak hover:outline-n-slate-6 focus:outline-n-blue-9"
            >
              <option value="" disabled>
                {{ $t('PIPELINE.DEAL_STATUS.LOST_MODAL.REASON_PLACEHOLDER') }}
              </option>
              <option
                v-for="reason in commonReasons"
                :key="reason.id"
                :value="reason.id"
              >
                {{ reason.label }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <fluent-icon
                icon="chevron-down"
                size="16"
                class="text-n-slate-11"
              />
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex gap-3">
          <button
            class="flex-1 rounded-lg border border-n-weak px-4 py-2 text-label font-medium text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12 disabled:opacity-50"
            :disabled="submitting"
            @click="handleCancel"
          >
            {{ $t('PIPELINE.DEAL_STATUS.LOST_MODAL.CANCEL') }}
          </button>
          <button
            aria-describedby="mark-lost-reason-select"
            class="flex-1 rounded-lg bg-n-ruby-9 px-4 py-2 text-label font-medium text-white hover:brightness-110 disabled:opacity-50"
            :disabled="submitting || !selectedReason"
            @click="handleConfirm"
          >
            <span v-if="!submitting">
              {{ $t('PIPELINE.DEAL_STATUS.LOST_MODAL.CONFIRM') }}
            </span>
            <span
              v-else
              role="status"
              aria-live="polite"
              class="flex items-center justify-center gap-2"
            >
              <div
                class="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
              />
              {{ $t('PIPELINE.DEAL_STATUS.LOST_MODAL.PROCESSING') }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
